import React, { useState, useRef, useEffect } from 'react';
import { Stack } from '@fluentui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faChevronDown, faUsers } from '@fortawesome/pro-light-svg-icons';
import { IOperationItem } from '../types';
import Text from '../../../typography/text';
import OrderService from '../../../../services/orders/order';

interface ISalesBadgeProps {
    item: IOperationItem;
    size?: 'compact' | 'normal';
}

export const SalesBadge: React.FC<ISalesBadgeProps> = ({ item, size = 'normal' }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const orders = item.orders || [];
    const hasMultipleOrders = orders.length > 1;

    // Multi-order case
    if (hasMultipleOrders) {
        // Collect orders with their sales person
        const orderSalesList: { orderNumber: string; salesName: string }[] = [];
        const uniqueSales = new Set<string>();

        orders.forEach((o) => {
            const sName = o.salesName || (o.sales && o.sales.length > 0 ? o.sales[0].name : undefined) || item.salesName;
            if (sName) {
                uniqueSales.add(sName);
                orderSalesList.push({
                    orderNumber: o.orderNumber,
                    salesName: sName,
                });
            }
        });

        const uniqueSalesArray = Array.from(uniqueSales);

        if (uniqueSalesArray.length === 0) {
            return (
                <Text size={size === 'compact' ? 'xsmall' : 'small'} style={{ color: '#94a3b8' }}>
                    -
                </Text>
            );
        }

        if (uniqueSalesArray.length === 1) {
            const primaryName = uniqueSalesArray[0];
            const initial = primaryName.trim().charAt(0).toUpperCase();

            return (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: size === 'compact' ? '20px' : '24px',
                            height: size === 'compact' ? '20px' : '24px',
                            borderRadius: '50%',
                            backgroundColor: '#eff6ff',
                            color: '#2563eb',
                            border: '1px solid #bfdbfe',
                            fontSize: size === 'compact' ? '10px' : '11px',
                            fontWeight: 600,
                            flexShrink: 0,
                        }}
                    >
                        {initial}
                    </span>
                    <Text size={size === 'compact' ? 'xsmall' : 'small'} style={{ fontWeight: 600, color: '#0f172a' }}>
                        {primaryName}
                    </Text>
                </div>
            );
        }

        // Multiple distinct sales across orders
        const primaryName = uniqueSalesArray[0];
        const extraCount = uniqueSalesArray.length - 1;
        const initial = primaryName.trim().charAt(0).toUpperCase();

        return (
            <div style={{ position: 'relative', display: 'inline-block' }} ref={popoverRef}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: size === 'compact' ? '20px' : '24px',
                            height: size === 'compact' ? '20px' : '24px',
                            borderRadius: '50%',
                            backgroundColor: '#eff6ff',
                            color: '#2563eb',
                            border: '1px solid #bfdbfe',
                            fontSize: size === 'compact' ? '10px' : '11px',
                            fontWeight: 600,
                            flexShrink: 0,
                        }}
                    >
                        {initial}
                    </span>
                    <Text size={size === 'compact' ? 'xsmall' : 'small'} style={{ fontWeight: 600, color: '#0f172a' }}>
                        {primaryName}
                    </Text>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(!isOpen);
                        }}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            padding: '1px 5px',
                            fontSize: '10px',
                            fontWeight: 600,
                            color: '#475569',
                            cursor: 'pointer',
                        }}
                        title="Click to view all sales persons for this voyage"
                    >
                        <span>+{extraCount} Sales</span>
                        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '8px' }} />
                    </button>
                </div>

                {/* Popover */}
                {isOpen && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + 6px)',
                            left: 0,
                            zIndex: 9999,
                            minWidth: '220px',
                            maxWidth: '280px',
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                            padding: '10px 12px',
                            fontSize: '12px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '6px', borderBottom: '1px solid #f1f5f9', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 600, color: '#334155', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Sales by Order
                            </span>
                            <FontAwesomeIcon icon={faUsers} style={{ color: '#94a3b8', fontSize: '12px' }} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                            {orderSalesList.map((entry, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '4px 6px',
                                        borderRadius: '4px',
                                        background: idx % 2 === 0 ? '#f8fafc' : 'transparent',
                                    }}
                                >
                                    <span style={{ fontWeight: 500, color: '#0284c7', fontSize: '11px' }}>
                                        #{OrderService.getOrderNumber(entry.orderNumber)}
                                    </span>
                                    <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '11px' }}>
                                        {entry.salesName}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Single order case
    const salesName = item.salesName || (item.sales && item.sales.length > 0 ? item.sales[0].name : undefined);

    if (!salesName) {
        return (
            <Text size={size === 'compact' ? 'xsmall' : 'small'} style={{ color: '#94a3b8' }}>
                -
            </Text>
        );
    }

    const initial = salesName.trim().charAt(0).toUpperCase();

    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: size === 'compact' ? '20px' : '24px',
                    height: size === 'compact' ? '20px' : '24px',
                    borderRadius: '50%',
                    backgroundColor: '#eff6ff',
                    color: '#2563eb',
                    border: '1px solid #bfdbfe',
                    fontSize: size === 'compact' ? '10px' : '11px',
                    fontWeight: 600,
                    flexShrink: 0,
                }}
            >
                {initial}
            </span>
            <Text size={size === 'compact' ? 'xsmall' : 'small'} style={{ fontWeight: 600, color: '#0f172a' }}>
                {salesName}
            </Text>
        </div>
    );
};

